import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Link,
    List,
    ListItem,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import HistoryIcon from "@mui/icons-material/History";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useActivityFeed, ActivityLevel, ActivityLogRow } from "../../hooks/useActivityFeed";
import { isInternal } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { usePortalPath } from '../../hooks/usePortalPath';

// Brand gold, matching ProjectTimeline — via theme tokens so it flips modes.
const ACCENT = "var(--accent)";
const NEUTRAL = "var(--text-2)";

interface CategoryMeta {
    label: string;
    color: string;
    Icon: React.ElementType;
}

// Keep keys in sync with CATEGORY in the API's activityRegistry.js.
const CATEGORY_META: Record<string, CategoryMeta> = {
    brief: { label: "Briefs", color: "#B4881F", Icon: DescriptionOutlinedIcon },
    status: { label: "Status", color: "#2F6FB0", Icon: FlagOutlinedIcon },
    // "Edits" is the one gold label in the feed (approved designs 16a/16b).
    project: { label: "Edits", color: "var(--accent-text)", Icon: EditOutlinedIcon },
    deliverable: { label: "Deliverables", color: "#7A54C0", Icon: MovieOutlinedIcon },
    comment: { label: "Comments", color: "#128577", Icon: ChatBubbleOutlineIcon },
    assignment: { label: "Assignments", color: "#2E8B57", Icon: GroupOutlinedIcon },
    shipping: { label: "Shipping", color: "#C4741A", Icon: LocalShippingOutlinedIcon },
    account: { label: "Accounts", color: "#475569", Icon: PersonAddOutlinedIcon },
    order: { label: "Order", color: "#8A6D1F", Icon: DescriptionOutlinedIcon },
    other: { label: "Other", color: "#6B7280", Icon: HistoryIcon }
};

const metaFor = (category: string): CategoryMeta => CATEGORY_META[category] || CATEGORY_META.other;

// Categories offered in the Filter dropdown.
const FILTER_CATEGORIES = [
    "status",
    "deliverable",
    "comment",
    "assignment",
    "brief",
    "shipping",
    "project",
    // 'account' rows (brand/expert registered) and 'order' rows (order/proposal
    // create/update/convert via the API's ACTION.ORDER_*/PROPOSAL_* kinds) are
    // both emitted, so they must be filterable too.
    "account",
    "order"
];

function relativeTime(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diff = Date.now() - then;
    const s = Math.round(diff / 1000);
    if (s < 45) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.round(d / 7);
    if (w < 5) return `${w}w ago`;
    return new Date(iso).toLocaleDateString();
}

function dayLabel(iso: string): string {
    const d = new Date(iso);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    const sameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(d, today)) return "Today";
    if (sameDay(d, yest)) return "Yesterday";
    return d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric"
    });
}

// Best-effort deep link into the object the event concerns (for the message).
// Some payloads store the bare projects base ('/video-production/projects') that
// needs the project id appended; others store a COMPLETE path already embedding
// ids or a query string (e.g. '/admin/projects/12/experts/3/confirmation',
// '/videos/tdc?video=9&selectedTab=review', '/brands/5'). Only the bare base gets
// the id appended — every other path is used as-is, so we never double the id.
const PROJECTS_BASE = "/video-production/projects";
function deepLinkFor(row: ActivityLogRow): string | null {
    const meta = row.metadata || {};
    const path = typeof meta.path === "string" ? meta.path : "";
    // A deleted project's detail page 404s — never append the (now-gone) id; use
    // the stored base path (the projects index). Mirrors the backend
    // buildSlackDeepLink guard. (brand_project_id is also nulled by the FK's
    // onDelete:SET NULL, but guard explicitly so a future change can't regress.)
    if (row.action === "project.deleted") return path || PROJECTS_BASE;
    if (path === PROJECTS_BASE && row.brand_project_id) {
        let link = `${PROJECTS_BASE}/${row.brand_project_id}`;
        // A bulk approve of exactly one file is as specific as a single-file
        // event — same rule as the backend's buildNotificationDeepLink.
        const fileId =
            meta.fileId ||
            (Array.isArray(meta.fileIds) && meta.fileIds.length === 1 ? meta.fileIds[0] : null);
        if (fileId) {
            link += `?tab=downloads&review=${fileId}`;
            // Comment anchor + seek offset, the same as the Slack and email
            // links — except for a deleted comment: there is nothing left to
            // focus, and a dangling `comment` param stops the panel from doing
            // its usual scroll to the newest comment.
            if (meta.commentId && row.action !== "comment.deleted") {
                link += `&comment=${meta.commentId}`;
                if (meta.timestamp != null) link += `&t=${meta.timestamp}`;
            }
        }
        return link;
    }
    if (path) return path;
    if (row.brand_project_id) {
        return `${PROJECTS_BASE}/${row.brand_project_id}`;
    }
    return null;
}

// Small clickable id link inside a row. Stops propagation so it applies the id
// filter in place instead of triggering the message's navigation link.
function IdLink({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <Link
            component="button"
            type="button"
            underline="hover"
            onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                onClick();
            }}
            sx={{
                color: NEUTRAL,
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.4,
                verticalAlign: "baseline"
            }}
        >
            {label}
        </Link>
    );
}

// How much filtering chrome to show. 'full' = Filter dropdown + id inputs
// (dedicated / admin), 'minimal' = Filter dropdown only (embedded feeds),
// 'none' = no controls.
export type FilterMode = "full" | "minimal" | "none";

export interface ActivityFeedProps {
    level: ActivityLevel;
    id?: number | string | null;
    title?: string;
    filters?: FilterMode;
    // Persist active filters to the URL (shareable / survives refresh).
    syncUrl?: boolean;
    pollMs?: number;
    maxHeight?: number | string;
    // Render without the outer Paper (for embedding in an existing card).
    disableContainer?: boolean;
}

const intOrNull = (raw: string | null): number | null => {
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
};

/**
 * Reusable, scannable activity feed. One component powers the project detail,
 * brand/RMN dashboard, and admin (global) views — pass the matching level + id.
 *
 * Filtering (all server-side — every change refetches a fresh batch):
 *   - Filter dropdown: categories + "Major only".
 *   - Order / Project / Deliverable id inputs (full mode).
 *   - Clickable ids on each row set the same id filters in place.
 *   - Active filters render as removable chips below the controls.
 */
export default function ActivityFeed({
    level,
    id,
    title = "Activity",
    filters = "full",
    syncUrl = false,
    pollMs,
    maxHeight = 460,
    disableContainer = false
}: ActivityFeedProps) {
    const { role } = useUser();
    const { p } = usePortalPath();
    const hasControls = filters !== "none";
    const showIdInputs = filters === "full";
    // Ids are redundant on a single-entity feed (project sidebar / file view).
    const showRowIds = level !== "project" && level !== "file";

    const [searchParams, setSearchParams] = useSearchParams();

    const seed = syncUrl;
    const [activeCats, setActiveCats] = useState<string[]>(() => {
        if (!seed) return [];
        const raw = searchParams.get("cat");
        return raw ? raw.split(",").filter(Boolean) : [];
    });
    const [majorOnly, setMajorOnly] = useState<boolean>(() => (seed ? searchParams.get("sev") === "major" : false));
    const [orderId, setOrderId] = useState<number | null>(() => (seed ? intOrNull(searchParams.get("oid")) : null));
    const [projectId, setProjectId] = useState<number | null>(() => (seed ? intOrNull(searchParams.get("pid")) : null));
    const [fileId, setFileId] = useState<number | null>(() => (seed ? intOrNull(searchParams.get("did")) : null));

    // Mirror the active filters back into the URL when syncUrl is on.
    useEffect(() => {
        if (!syncUrl) return;
        const next = new URLSearchParams(searchParams);
        const setOrDelete = (key: string, val: string | null) => {
            if (val) next.set(key, val);
            else next.delete(key);
        };
        setOrDelete("cat", activeCats.length ? activeCats.join(",") : null);
        setOrDelete("sev", majorOnly ? "major" : null);
        setOrDelete("oid", orderId ? String(orderId) : null);
        setOrDelete("pid", projectId ? String(projectId) : null);
        setOrDelete("did", fileId ? String(fileId) : null);
        setSearchParams(next, { replace: true });
        // Re-sync only when the filters themselves change.
    }, [activeCats, majorOnly, orderId, projectId, fileId, syncUrl]);

    const { rows, count, loading, error, hasMore, loadMore } = useActivityFeed({
        level,
        id,
        category: activeCats.length ? activeCats.join(",") : undefined,
        severity: majorOnly ? "major" : undefined,
        orderId,
        projectId,
        fileId,
        pollMs
    });

    // ── Filter dropdown ───────────────────────────────────────────────────
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const toggleCat = (cat: string) =>
        setActiveCats((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));

    // Local text for the id inputs; apply on Enter, then clear.
    const [orderInput, setOrderInput] = useState("");
    const [projectInput, setProjectInput] = useState("");
    const [deliverableInput, setDeliverableInput] = useState("");
    const applyIdInput = (raw: string, setter: (n: number | null) => void, clear: () => void) => {
        const n = parseInt(raw, 10);
        // Require a positive id: real anchor ids start at 1, and every downstream
        // filter check is truthiness-based, so accepting 0 would clear the input
        // while applying no filter (a silent no-op with no chip/count/URL change).
        if (!Number.isNaN(n) && n > 0) {
            setter(n);
            clear();
        }
    };

    const filterCount =
        activeCats.length + (majorOnly ? 1 : 0) + (orderId ? 1 : 0) + (projectId ? 1 : 0) + (fileId ? 1 : 0);

    // ── Active-filter chips (removable) ───────────────────────────────────
    type ActiveChip = { key: string; label: string; color: string; remove: () => void };
    const activeChips: ActiveChip[] = [
        ...activeCats.map((cat) => {
            const m = metaFor(cat);
            return {
                key: `cat:${cat}`,
                label: m.label,
                color: m.color,
                remove: () => toggleCat(cat)
            };
        }),
        ...(majorOnly ? [{ key: "sev", label: "Major only", color: ACCENT, remove: () => setMajorOnly(false) }] : []),
        ...(orderId
            ? [
                  {
                      key: "oid",
                      label: `Order #${orderId}`,
                      color: CATEGORY_META.order.color,
                      remove: () => setOrderId(null)
                  }
              ]
            : []),
        ...(projectId
            ? [
                  {
                      key: "pid",
                      label: `Project #${projectId}`,
                      color: CATEGORY_META.status.color,
                      remove: () => setProjectId(null)
                  }
              ]
            : []),
        ...(fileId
            ? [
                  {
                      key: "did",
                      label: `Deliverable #${fileId}`,
                      color: CATEGORY_META.deliverable.color,
                      remove: () => setFileId(null)
                  }
              ]
            : [])
    ];

    // Group rows by calendar day for subheaders.
    const grouped = useMemo(() => {
        const out: Array<{ day: string; items: ActivityLogRow[] }> = [];
        let currentKey = "";
        rows.forEach((row) => {
            const key = new Date(row.created_at).toDateString();
            if (key !== currentKey) {
                out.push({ day: dayLabel(row.created_at), items: [] });
                currentKey = key;
            }
            out[out.length - 1].items.push(row);
        });
        return out;
    }, [rows]);

    const titleBlock = (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <HistoryIcon sx={{ color: ACCENT, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
                {title}
            </Typography>
            {count > 0 && (
                <Typography variant="caption" color="text.secondary">
                    {count}
                </Typography>
            )}
        </Stack>
    );

    const idInputSx = { width: 128, "& .MuiInputBase-input": { fontSize: 12, py: 0.75 } };

    // All the filters live in one container: when the header row runs out of
    // room the whole group drops below the title, then its own items wrap.
    const controls = hasControls && (
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    sx={{
                        textTransform: "none",
                        color: "text.secondary",
                        borderColor: "divider",
                        py: 0.4
                    }}
                >
                    Filter{filterCount ? ` (${filterCount})` : ""}
                </Button>

                {showIdInputs && (
                    <>
                        <TextField
                            size="small"
                            placeholder="Order id"
                            value={orderInput}
                            onChange={(e) => setOrderInput(e.target.value.replace(/[^0-9]/g, ""))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") applyIdInput(orderInput, setOrderId, () => setOrderInput(""));
                            }}
                            sx={idInputSx}
                        />
                        <TextField
                            size="small"
                            placeholder="Project id"
                            value={projectInput}
                            onChange={(e) => setProjectInput(e.target.value.replace(/[^0-9]/g, ""))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter")
                                    applyIdInput(projectInput, setProjectId, () => setProjectInput(""));
                            }}
                            sx={idInputSx}
                        />
                        <TextField
                            size="small"
                            placeholder="Deliverable id"
                            value={deliverableInput}
                            onChange={(e) => setDeliverableInput(e.target.value.replace(/[^0-9]/g, ""))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter")
                                    applyIdInput(deliverableInput, setFileId, () => setDeliverableInput(""));
                            }}
                            sx={idInputSx}
                        />
                    </>
                )}
            </Stack>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                MenuListProps={{ dense: true }}
            >
                {FILTER_CATEGORIES.map((cat) => {
                    const m = metaFor(cat);
                    return (
                        <MenuItem key={cat} onClick={() => toggleCat(cat)} sx={{ py: 0.25 }}>
                            <Checkbox
                                size="small"
                                checked={activeCats.includes(cat)}
                                sx={{ p: 0.5, mr: 0.5, color: m.color, "&.Mui-checked": { color: m.color } }}
                            />
                            <Typography variant="body2">{m.label}</Typography>
                        </MenuItem>
                    );
                })}
                <Divider />
                <MenuItem onClick={() => setMajorOnly((v) => !v)} sx={{ py: 0.25 }}>
                    <Checkbox
                        size="small"
                        checked={majorOnly}
                        sx={{ p: 0.5, mr: 0.5, color: ACCENT, "&.Mui-checked": { color: ACCENT } }}
                    />
                    <Typography variant="body2">Major only</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );

    let headerMb = 0;
    if (hasControls) headerMb = activeChips.length ? 0.75 : 1;

    // One row: title on the left, filters on the right; wraps on narrow widths.
    const header = (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            useFlexGap
            sx={{ columnGap: 2, rowGap: 1, mb: headerMb }}
        >
            {titleBlock}
            {controls}
        </Stack>
    );

    const chipRow = activeChips.length > 0 && (
        <Box sx={{ mb: 1 }}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {activeChips.map((c) => (
                    <Chip
                        key={c.key}
                        size="small"
                        label={c.label}
                        onClick={c.remove}
                        onDelete={c.remove}
                        sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: "6px",
                            color: "#fff",
                            bgcolor: c.color,
                            "& .MuiChip-deleteIcon": {
                                color: "rgba(255,255,255,0.85)",
                                fontSize: 15,
                                "&:hover": { color: "#fff" }
                            }
                        }}
                    />
                ))}
            </Stack>
        </Box>
    );

    const body = (
        <Box sx={{ maxHeight, overflowY: "auto" }}>
            {error && (
                <Typography variant="body2" color="error" sx={{ p: 1 }}>
                    {error}
                </Typography>
            )}
            {!error && rows.length === 0 && !loading && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No activity yet.
                </Typography>
            )}
            <List
                dense
                disablePadding
                sx={{
                    display: "grid",
                    // Full-width feeds show two activities per row within a day;
                    // on small screens (< md) they collapse to a single column
                    // so the cards stack. The day header and divider below span
                    // the full width via gridColumn "1 / -1".
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    columnGap: 1,
                    alignItems: "start"
                }}
            >
                {grouped.map((group, gi) => (
                    <React.Fragment key={`${group.day}-${gi}`}>
                        <ListSubheader
                            sx={{
                                gridColumn: "1 / -1",
                                bgcolor: "background.paper",
                                lineHeight: "28px",
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: 0.4,
                                textTransform: "uppercase",
                                color: "text.secondary"
                            }}
                        >
                            {group.day}
                        </ListSubheader>
                        {group.items.map((row) => {
                            const m = metaFor(row.category);
                            // The path comes from the SERVER, and for internal
                            // staff it can name an internal-only page
                            // ('/brands/5', '/admin/projects/...'). Those must
                            // NOT take a portal prefix — a staffer reading this
                            // feed inside a workspace has a non-empty basePath,
                            // and prefixing would 404. A brand client's feed
                            // only ever carries brand-facing paths, so this is
                            // the one place in the sweep where the correct
                            // prefix depends on WHO is looking rather than on
                            // where the code runs.
                            const rawLink = deepLinkFor(row);
                            const link = rawLink && !isInternal(role) ? p(rawLink) : rawLink;
                            const summaryNode = (
                                <Typography
                                    component="span"
                                    variant="body2"
                                    sx={{ fontWeight: row.severity === "major" ? 600 : 400 }}
                                >
                                    {row.actor_display_name && (
                                        <Typography component="span" variant="body2" fontWeight={700}>
                                            {row.actor_display_name}{" "}
                                        </Typography>
                                    )}
                                    {row.summary}
                                </Typography>
                            );
                            return (
                                <ListItem
                                    key={row.id}
                                    alignItems="flex-start"
                                    sx={{ py: 0.5, px: 1 }}
                                    secondaryAction={
                                        <Tooltip title={new Date(row.created_at).toLocaleString()}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    display: "block",
                                                    maxWidth: 72,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    textAlign: "right",
                                                }}
                                            >
                                                {relativeTime(row.created_at)}
                                            </Typography>
                                        </Tooltip>
                                    }
                                >
                                    <Box sx={{ mt: "3px", mr: 1, color: m.color, display: "flex" }}>
                                        <m.Icon sx={{ fontSize: 18 }} />
                                    </Box>
                                    <ListItemText
                                        sx={{ my: 0, pr: "88px" }}
                                        secondaryTypographyProps={{ component: "div" }}
                                        primary={
                                            link ? (
                                                <Link
                                                    component={RouterLink}
                                                    to={link}
                                                    underline="hover"
                                                    color="inherit"
                                                >
                                                    {summaryNode}
                                                </Link>
                                            ) : (
                                                summaryNode
                                            )
                                        }
                                        secondary={
                                            <Stack
                                                direction="row"
                                                spacing={0.75}
                                                alignItems="center"
                                                flexWrap="wrap"
                                                useFlexGap
                                                sx={{ mt: 0.25 }}
                                            >
                                                <Typography variant="caption" sx={{ color: m.color, fontWeight: 600 }}>
                                                    {m.label}
                                                </Typography>
                                                {showRowIds && row.brand_order_id && (
                                                    <IdLink
                                                        label={`Order #${row.brand_order_id}`}
                                                        onClick={() => setOrderId(row.brand_order_id)}
                                                    />
                                                )}
                                                {showRowIds && row.brand_project_id && (
                                                    <IdLink
                                                        label={`Project #${row.brand_project_id}`}
                                                        onClick={() => setProjectId(row.brand_project_id)}
                                                    />
                                                )}
                                                {showRowIds && row.s3_file_id && (
                                                    <IdLink
                                                        label={`Deliverable #${row.s3_file_id}`}
                                                        onClick={() => setFileId(row.s3_file_id)}
                                                    />
                                                )}
                                                {row.slack_notified && (
                                                    <Chip
                                                        label="Slack"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            height: 16,
                                                            fontSize: 10,
                                                            "& .MuiChip-label": { px: 0.5 }
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                        <Divider component="li" sx={{ gridColumn: "1 / -1" }} />
                    </React.Fragment>
                ))}
            </List>

            <Box sx={{ textAlign: "center", py: 1 }}>
                {loading && <CircularProgress size={20} sx={{ color: ACCENT }} />}
                {!loading && hasMore && (
                    <Button size="small" onClick={loadMore} sx={{ color: ACCENT }}>
                        Load more
                    </Button>
                )}
            </Box>
        </Box>
    );

    const inner = (
        <>
            {header}
            {chipRow}
            {body}
        </>
    );

    if (disableContainer) {
        return <Box>{inner}</Box>;
    }

    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            {inner}
        </Paper>
    );
}
